import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../utils/api";
import styles from "./NewOrganizationPage.module.scss";
import { useAppContext } from "../../context/AppContext";
import { useAuth } from "../../context/AuthContext";
import TabbedFormLayout from "../../components/TabbedFormLayout/TabbedFormLayout";
import CustomInput from "../../components/Inputs/CustomInput";
import CustomCalendar from "../../components/CustomCalendar/CustomCalendar";
import { AxiosResponse, AxiosErrorType } from "../../types";

// Define interfaces
interface Organization {
  name: string;
  adminuserid: string;
  primary_payment_account_id: string;
}

interface OrganizationInfo {
  full_business_name: string;
  business_dba: string;
  business_address: string;
  main_business_phone: string;
  fax_number: string;
  support_email: string;
  business_type: string;
  business_industry: string;
  website_url: string;
  established_date: string;
  tax_id_number: string;
  registration_number: string;
  operating_hours: {
    monday: string;
    tuesday: string;
    wednesday: string;
    thursday: string;
    friday: string;
    saturday: string;
    sunday: string;
  };
  business_logo_url: string;
}

interface User {
  id: string;
  email: string;
}

interface App {
  id: string;
  name: string;
}

interface UserAssignment {
  email: string;
  systemrole: string;
}

interface AppAssignment {
  appId: string;
  accessLevel: string;
}

interface AuthContext {
  user: { id: string; issuperadmin: boolean };
}

interface AppContext {
  setEditOrganizationName: (name: string) => void;
}

export default function NewOrganizationPage() {
  const navigate = useNavigate();
  const { setEditOrganizationName } = useAppContext() as AppContext;
  const { user } = useAuth() as AuthContext;
  const [orgData, setOrgData] = useState<Organization>({
    name: "",
    adminuserid: user.id,
    primary_payment_account_id: "",
  });
  const [orgInfo, setOrgInfo] = useState<OrganizationInfo>({
    full_business_name: "",
    business_dba: "",
    business_address: "",
    main_business_phone: "",
    fax_number: "",
    support_email: "",
    business_type: "",
    business_industry: "",
    website_url: "",
    established_date: "",
    tax_id_number: "",
    registration_number: "",
    operating_hours: { monday: "", tuesday: "", wednesday: "", thursday: "", friday: "", saturday: "", sunday: "" },
    business_logo_url: "",
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoFileName, setLogoFileName] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [users, setUsers] = useState<User[]>([]);
  const [apps, setApps] = useState<App[]>([]);
  const [userAssignments, setUserAssignments] = useState<UserAssignment[]>([{ email: "", systemrole: "member" }]);
  const [appAssignments, setAppAssignments] = useState<AppAssignment[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersRes, appsRes] = await Promise.all([
          api.post<AxiosResponse<{ users: User[] }>>("/core/user/list", {}),
          api.post<AxiosResponse<App[]>>("/core/organization/app/list", {})
        ]);
        setUsers(usersRes.data.data.users || []);
        setApps(appsRes.data.data || []);
      } catch (err: unknown) {
        const axiosErr = err as AxiosErrorType;
        const errorMessage = axiosErr.response?.data?.message || (err as Error).message || "Failed to load users or apps";
        console.error("Failed to fetch users or apps:", errorMessage);
        setError(errorMessage);
      }
    };
    fetchData();
  }, []);

  const handleOrgSubmit = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.post<AxiosResponse<{ id: string }>>("/core/organization/create", {
        name: orgData.name,
        adminuserid: orgData.adminuserid,
        primary_payment_account_id: orgData.primary_payment_account_id,
        full_business_name: orgInfo.full_business_name,
        business_dba: orgInfo.business_dba,
        business_address: orgInfo.business_address,
        main_business_phone: orgInfo.main_business_phone,
        fax_number: orgInfo.fax_number,
        support_email: orgInfo.support_email,
        business_type: orgInfo.business_type,
        business_industry: orgInfo.business_industry,
        website_url: orgInfo.website_url,
        established_date: orgInfo.established_date,
        tax_id_number: orgInfo.tax_id_number,
        registration_number: orgInfo.registration_number,
        operating_hours: orgInfo.operating_hours,
        userAssignments,
        appAssignments
      });
      const newOrgId = response.data.data.id;

      if (logoFile) {
        const formData = new FormData();
        formData.append("logo", logoFile);
        formData.append("organizationId", newOrgId);
        await api.post("/core/organization/upload-logo", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      }

      setEditOrganizationName(orgData.name);
      navigate(`/organizations/edit/${newOrgId}`);
    } catch (err: unknown) {
      const axiosErr = err as AxiosErrorType;
      const errorMessage = axiosErr.response?.data?.message || (err as Error).message || "Failed to create organization";
      console.error("Create organization failed:", errorMessage, axiosErr.response?.data);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setLogoFile(e.target.files[0]);
      setLogoFileName(e.target.files[0].name);
    }
  };

  const addUserAssignment = () => {
    setUserAssignments([...userAssignments, { email: "", systemrole: "member" }]);
  };

  const updateUserAssignment = (index: number, field: "email" | "systemrole", value: string) => {
    const updated = [...userAssignments];
    updated[index] = { ...updated[index], [field]: value };
    setUserAssignments(updated);
  };

  const removeUserAssignment = (index: number) => {
    setUserAssignments(userAssignments.filter((_, i) => i !== index));
  };

  const updateAppAssignment = (appId: string, accessLevel: string) => {
    const updated = appAssignments.filter(a => a.appId !== appId);
    if (accessLevel !== "") {
      updated.push({ appId, accessLevel });
    }
    setAppAssignments(updated);
  };

  const fieldTypeMap: Record<string, "text" | "email" | "tel" | "ein"> = {
    main_business_phone: "tel",
    support_email: "email",
    tax_id_number: "ein",
    registration_number: "text",
    full_business_name: "text",
    business_dba: "text",
    business_address: "text",
    business_type: "text",
    business_industry: "text",
    website_url: "text",
    fax_number: "tel"
  };

  const tabs = [
    {
      label: "Details",
      content: (
        <div className={styles.section}>
          <h3>New Organization Details</h3>
          {error && <div className={styles.error}>{error}</div>}
          <div className={styles.formGrid}>
            <CustomInput
              label="Name"
              value={orgData.name}
              onChange={(val: string) => setOrgData({ ...orgData, name: val })}
              required
              type="text"
              size="half"
            />
            <CustomInput
              label="Admin User ID"
              value={orgData.adminuserid}
              onChange={(val: string) => setOrgData({ ...orgData, adminuserid: val })}
              disabled={!user.issuperadmin}
              type="text"
              size="half"
            />
            <CustomInput
              label="Primary Payment Account ID"
              value={orgData.primary_payment_account_id}
              onChange={(val: string) => setOrgData({ ...orgData, primary_payment_account_id: val })}
              disabled={!user.issuperadmin}
              type="text"
              size="half"
            />
            <div className={styles.inputWrapper}>
              <CustomInput
                label="Logo"
                value={logoFileName}
                onChange={() => {}} // Read-only
                type="text"
                size="half"
                disabled
              />
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className={styles.hiddenFileInput}
              />
            </div>
            {Object.keys(orgInfo).map((key) => {
              if (key === "operating_hours") {
                return (
                  <div key={key} className={styles.fullRow}>
                    <label className={styles.label}>Operating Hours</label>
                    {Object.keys(orgInfo.operating_hours).map((day) => (
                      <CustomInput
                        key={day}
                        label={day.charAt(0).toUpperCase() + day.slice(1)}
                        value={orgInfo.operating_hours[day as keyof OrganizationInfo['operating_hours']]}
                        onChange={(val: string) =>
                          setOrgInfo({
                            ...orgInfo,
                            operating_hours: { ...orgInfo.operating_hours, [day]: val }
                          })
                        }
                        placeholder="e.g., 9:00 AM - 5:00 PM"
                        type="text"
                        size="full"
                      />
                    ))}
                  </div>
                );
              }
              if (key === "business_logo_url") return null;
              if (key === "established_date") {
                return (
                  <CustomCalendar
                    key={key}
                    label="Established Date"
                    value={orgInfo.established_date}
                    onChange={(val: string) => setOrgInfo({ ...orgInfo, established_date: val })}
                    required
                    size="half"
                  />
                );
              }
              const fieldType: "text" | "email" | "tel" | "ein" = fieldTypeMap[key] || "text";
              return (
                <CustomInput
                  key={key}
                  label={key.split("_").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ")}
                  value={orgInfo[key as keyof Omit<OrganizationInfo, 'operating_hours' | 'business_logo_url'>]}
                  onChange={(val: string) => setOrgInfo({ ...orgInfo, [key]: val })}
                  type={fieldType}
                  size={fieldType === "text" ? "full" : "half"}
                  required={key === "full_business_name" || key === "business_address"}
                />
              );
            })}
          </div>
        </div>
      )
    },
    {
      label: "Team Members",
      content: (
        <div className={styles.section}>
          <h3>Add Team Members</h3>
          <div className={styles.formGrid}>
            {userAssignments.map((assignment, index) => (
              <div key={index} className={styles.userAssignment}>
                <CustomInput
                  label="User Email"
                  value={assignment.email}
                  onChange={(val: string) => updateUserAssignment(index, "email", val)}
                  type="email"
                  size="half"
                />
                <CustomInput
                  label="Role"
                  value={assignment.systemrole}
                  onChange={(val: string) => updateUserAssignment(index, "systemrole", val)}
                  type="select"
                  options={[{ value: "member", label: "Member" }, { value: "admin", label: "Admin" }]}
                  size="half"
                />
                <button
                  className={styles.removeButton}
                  onClick={() => removeUserAssignment(index)}
                  disabled={userAssignments.length === 1}
                >
                  Remove
                </button>
              </div>
            ))}
            <button className={styles.addButton} onClick={addUserAssignment}>
              Add User
            </button>
          </div>
        </div>
      )
    },
    {
      label: "App Access",
      content: (
        <div className={styles.section}>
          <h3>Configure App Access</h3>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>App Name</th>
                <th>Access Level</th>
              </tr>
            </thead>
            <tbody>
              {apps.map((app) => (
                <tr key={app.id}>
                  <td>{app.name}</td>
                  <td>
                    <CustomInput
                      label="Access Level"
                      value={appAssignments.find(a => a.appId === app.id)?.accessLevel || ""}
                      onChange={(val: string) => updateAppAssignment(app.id, val)}
                      type="select"
                      options={[
                        { value: "", label: "None" },
                        { value: "read", label: "Read" },
                        { value: "write", label: "Write" }
                      ]}
                      size="full"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
    }
  ];

  return (
    <div className={styles.container}>
      <div className={styles.title}>Create New Organization</div>
      {loading && <div className={styles.loading}>Creating...</div>}
      <TabbedFormLayout
        tabs={tabs}
        onSave={handleOrgSubmit}
        onCancel={() => navigate("/organizations")}
        showActions={true}
      />
    </div>
  );
}